import { useRef, useState, useEffect } from 'react';
import Navigation from '@/shared/components/home/Navigation';
import CalendlyScheduleDialog from '@/shared/components/home/CalendlyScheduleDialog';
import CTAButton from '@/shared/components/home/CTAButton';
import SocialProofBar from '@/shared/components/home/SocialProofBar';
import ProductStorySection from '@/shared/components/home/ProductStorySection';
import ValuePropositionSection from '@/shared/components/home/ValuePropositionSection';
import FeatureNarrativeSection from '@/shared/components/home/FeatureNarrativeSection';
import IndustrySection from '@/shared/components/home/IndustrySection';
import CredibilitySection from '@/shared/components/home/CredibilitySection';
import HowItWorksSection from '@/shared/components/home/HowItWorksSection';
import DemoRequestDialog from '@/shared/components/home/DemoRequestDialog';
import MidPageCTASection from '@/shared/components/home/MidPageCTASection';
import ContactFormSection from '@/shared/components/home/ContactFormSection';
import { INDUSTRIES, DEMO_SECTION } from '@/shared/components/home/constants';

const HERO_CLIPS = [
  '/static/clips/alexan_clip.mp4',
  '/static/clips/central_clip.mp4',
];

const SLOW_MO_RATE = 0.25;
const FADE_DURATION_MS = 200;

const IronviewHomepage = () => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const [clipIndex, setClipIndex] = useState(0);
  const [fading, setFading] = useState(false);

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;
    video.playbackRate = SLOW_MO_RATE;
    setFading(false);
  }, [clipIndex]);

  const handleVideoEnded = () => {
    setFading(true);
    setTimeout(() => {
      setClipIndex((prev) => (prev + 1) % HERO_CLIPS.length);
    }, FADE_DURATION_MS);
  };

  const scrollToSection = (sectionId: string) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <div className="min-h-screen bg-maintenance-dark">

      {/* ─── 1. HERO ─────────────────────────────────────────────────── */}
      <section id="top" className="relative min-h-screen flex flex-col overflow-hidden">
        <video
          ref={videoRef}
          key={HERO_CLIPS[clipIndex]}
          className="absolute inset-0 w-full h-full object-cover transition-opacity duration-700"
          style={{ opacity: fading ? 0 : 1 }}
          src={HERO_CLIPS[clipIndex]}
          autoPlay
          muted
          playsInline
          onEnded={handleVideoEnded}
        />
        <div
          className="absolute inset-0 bg-cover bg-center bg-no-repeat -z-10"
          style={{ backgroundImage: `url('/static/images/sf2_1600.webp')` }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-maintenance-navy/80 via-maintenance-slate/75 to-maintenance-navy/70" />

        <div className="relative z-10 flex flex-col flex-1 p-2 md:p-5">
          <Navigation onNavigate={scrollToSection} />

          <div className="flex-1 flex flex-col justify-end px-6 md:px-12 lg:px-16 pb-16 md:pb-24">
            <div className="max-w-4xl">
              <h1 className="text-5xl sm:text-6xl md:text-7xl lg:text-8xl font-black text-white mb-6 leading-[0.95] tracking-tight">
                Take your<br />jobsite virtual.
              </h1>
              <p className="text-lg sm:text-xl md:text-2xl text-white/70 mb-10 leading-relaxed max-w-2xl">
                The most full-service, precise way to document and track your construction site — so every stakeholder stays aligned, on schedule, and under budget.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 items-start">
                <CalendlyScheduleDialog
                  triggerText="Schedule a Visit"
                  className="px-8 py-3 text-base"
                  fullWidthMobile={false}
                />
                <CTAButton
                  onClick={() => scrollToSection('features')}
                  className={`bg-white/10 text-white hover:bg-white/20 border border-white/30 hover:border-white/60 transition-colors px-8 py-3 text-base`}
                  fullWidthMobile={false}
                >
                  See How It Works
                </CTAButton>
              </div>
            </div>
          </div>
        </div>

        {/* Scroll hint */}
        <div className="absolute bottom-6 left-1/2 -translate-x-1/2 z-10 flex flex-col items-center gap-1 opacity-40">
          <div className="w-px h-10 bg-white animate-pulse" />
          <span className="text-white text-[10px] uppercase tracking-widest">Scroll</span>
        </div>
      </section>

      {/* ─── 2. SOCIAL PROOF ─────────────────────────────────────────── */}
      <SocialProofBar />

      {/* ─── 3. PRODUCT STORY ────────────────────────────────────────── */}
      <ProductStorySection />

      {/* ─── 4. VALUE PROPOSITION ────────────────────────────────────── */}
      <ValuePropositionSection />

      {/* ─── 5. FEATURE NARRATIVES ───────────────────────────────────── */}
      <FeatureNarrativeSection />

      {/* ─── 6. INDUSTRY SOLUTIONS ───────────────────────────────────── */}
      <IndustrySection
        industries={INDUSTRIES}
        onLearnMore={() => scrollToSection('get-started')}
      />

      {/* ─── 7. CREDIBILITY ──────────────────────────────────────────── */}
      <CredibilitySection />

      {/* ─── 8. HOW IT WORKS ─────────────────────────────────────────── */}
      <HowItWorksSection />

      {/* ─── 9. DEMOS ────────────────────────────────────────────────── */}
      <section id="demos" className="relative overflow-hidden min-h-screen flex items-center py-16 md:py-24">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: `url('${DEMO_SECTION.backgroundImage}')` }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-maintenance-dark/80 via-maintenance-dark/60 to-maintenance-dark/90" />

        <div className="relative z-10 container mx-auto px-6 md:px-12 lg:px-16">
          <div className="max-w-4xl mx-auto text-center">
            <p className="text-maintenance-cyan text-xs uppercase tracking-widest font-semibold mb-6">
              Interactive Demos
            </p>
            <h2 className="text-4xl sm:text-5xl md:text-6xl font-bold text-white mb-8 leading-tight">
              {DEMO_SECTION.title}
            </h2>
            <img
              src={DEMO_SECTION.clientsImage}
              alt="Construction firms that trust Ironview 360"
              className="w-full max-w-3xl mx-auto mb-10 opacity-90"
            />
            <p className="text-2xl sm:text-3xl font-semibold text-white mb-10">
              {DEMO_SECTION.subtitle}
            </p>
            <DemoRequestDialog />
          </div>
        </div>
      </section>

      {/* ─── 10. MID-PAGE CTA ────────────────────────────────────────── */}
      <MidPageCTASection />

      {/* ─── 11. CONTACT + FOOTER ────────────────────────────────────── */}
      <ContactFormSection onNavigate={scrollToSection} onBackToTop={() => scrollToSection('top')} />
    </div>
  );
};

export default IronviewHomepage;
